import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api, formatDate, timeAgo } from '@/lib/api';
import GigCard from '@/components/GigCard';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Clock, Award, Zap, TrendingUp, Plus, Bell, ChevronRight, Sparkles, Briefcase, DollarSign, FileText, Users, Mail } from 'lucide-react';
import { toast } from 'sonner';

export default function Home() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showSetup, setShowSetup] = useState(false);
  const [setupForm, setSetupForm] = useState({ role: 'teen', age: '', name: user?.name || '' });
  const [applications, setApplications] = useState([]);
  const [recommendations, setRecommendations] = useState([]);

  useEffect(() => {
    if (user?.needs_setup) setShowSetup(true);
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      const promises = [
        api.get('/opportunities?status=open'),
        api.get('/notifications'),
        api.get('/applications'),
      ];
      if (user?.role === 'teen') promises.push(api.get('/recommendations'));
      const results = await Promise.all(promises);
      setOpportunities(results[0].data.slice(0, 6));
      setNotifications(results[1].data.slice(0, 5));
      setApplications(results[2].data);
      if (results[3]) setRecommendations(results[3].data.slice(0, 4));
    } catch { /* ignore */ }
  };

  const handleSetup = async () => {
    try {
      await api.patch('/profile', {
        role: setupForm.role,
        age: setupForm.age ? parseInt(setupForm.age) : null,
        name: setupForm.name || user?.name,
        needs_setup: false,
      });
      await refreshUser();
      setShowSetup(false);
      toast.success('Profile set up!');
    } catch {
      toast.error('Failed to update profile');
    }
  };

  const markAllRead = async () => {
    try {
      await api.patch('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch { /* ignore */ }
  };

  const unreadCount = notifications.filter(n => !n.read).length;
  const isOrg = user?.role === 'organization';
  const xpProgress = user ? (user.xp % 100) : 0;

  // Org-specific stats
  const myTasks = opportunities.filter(o => o.creator_id === user?.user_id);
  const pendingApps = applications.filter(a => a.status === 'pending');
  const acceptedApps = applications.filter(a => a.status === 'accepted');

  return (
    <div className="px-4 pt-6 max-w-lg mx-auto" data-testid="home-dashboard">
      <SEO
        title="Dashboard"
        description="Your TeenHands dashboard — track volunteer hours, manage applications, discover new opportunities, and grow your impact."
        path="/home"
      />
      {/* Header */}
      <div className="flex items-center justify-between mb-6 animate-fade-in-up">
        <div>
          <p className="text-sm font-bold text-[#5C6B7F] uppercase tracking-wide">Welcome back</p>
          <h1 className="text-3xl font-bold text-[#011627]">{user?.name?.split(' ')[0] || 'Friend'}</h1>
        </div>
        <button onClick={() => navigate('/profile')} className="relative" data-testid="header-avatar">
          {(user?.picture_path || user?.picture) ? (
            <img src={user.picture_path ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${user.picture_path}` : user.picture} alt="avatar" className="w-12 h-12 rounded-2xl object-cover border-2 border-gray-100" />
          ) : (
            <div className="w-12 h-12 rounded-2xl bg-[#2EC4B6] flex items-center justify-center text-white font-bold text-lg">
              {user?.name?.[0] || '?'}
            </div>
          )}
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#E71D36] rounded-full text-white text-[10px] font-bold flex items-center justify-center">
              {unreadCount}
            </span>
          )}
        </button>
      </div>

      {/* Stats - Different for Teen vs Org */}
      {isOrg ? (
        <div className="grid grid-cols-2 gap-3 mb-6 animate-fade-in-up" style={{ animationDelay: '100ms' }}>
          {[
            { icon: Briefcase, value: myTasks.length, label: 'Posted', color: '#2EC4B6' },
            { icon: Users, value: pendingApps.length, label: 'Pending', color: '#FF9F1C' },
          ].map(({ icon: Icon, value, label, color }) => (
            <div key={label} className="bg-white rounded-2xl p-4 flex flex-col items-center border-2 border-gray-100 card-hover">
              <Icon size={22} style={{ color }} strokeWidth={2.5} />
              <span className="text-2xl font-bold text-[#011627] mt-1">{value}</span>
              <span className="text-[10px] font-bold text-[#9AA5B1] uppercase">{label}</span>
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-4 gap-3 mb-6 animate-fade-in-up" style={{ animationDelay: '100ms' }}>
            {[
              { icon: Clock, value: user?.total_hours?.toFixed(1) || '0', label: 'Vol Hrs', color: '#2EC4B6' },
              { icon: Briefcase, value: user?.total_gig_hours?.toFixed(1) || '0', label: 'Gig Hrs', color: '#FF9F1C' },
              { icon: DollarSign, value: `$${(user?.total_earnings || 0).toFixed(0)}`, label: 'Earned', color: '#6C63FF' },
              { icon: TrendingUp, value: user?.rating_avg?.toFixed(1) || '-', label: 'Rating', color: '#EC4899' },
            ].map(({ icon: Icon, value, label, color }) => (
              <div key={label} className="bg-white rounded-2xl p-3 flex flex-col items-center border-2 border-gray-100 card-hover">
                <Icon size={20} style={{ color }} strokeWidth={2.5} />
                <span className="text-xl font-bold text-[#011627] mt-1">{value}</span>
                <span className="text-[10px] font-bold text-[#9AA5B1] uppercase">{label}</span>
              </div>
            ))}
          </div>

          {/* XP Progress - Teen only */}
          <div className="bg-white rounded-2xl p-4 border-2 border-gray-100 mb-6 animate-fade-in-up" style={{ animationDelay: '200ms' }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-bold text-[#011627] flex items-center gap-2">
                <Zap size={16} className="text-[#FF9F1C]" /> Level {user?.level || 1}
              </span>
              <span className="text-xs font-bold text-[#9AA5B1]">{xpProgress}/100 XP</span>
            </div>
            <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#2EC4B6] to-[#6C63FF] rounded-full transition-all duration-500" style={{ width: `${xpProgress}%` }} />
            </div>
          </div>
        </>
      )}

      {/* Quick Actions */}
      <div className="flex gap-3 mb-6 animate-fade-in-up" style={{ animationDelay: '250ms' }}>
        <Button
          onClick={() => navigate('/explore')}
          className="flex-1 rounded-2xl py-5 bg-[#2EC4B6] hover:bg-[#25A296] text-white font-bold btn-bounce"
          data-testid="explore-btn"
        >
          <Sparkles size={18} className="mr-2" /> Find Gigs
        </Button>
        {isOrg ? (
          <Button
            onClick={() => navigate('/post-task')}
            className="flex-1 rounded-2xl py-5 bg-[#FF9F1C] hover:bg-[#E58E19] text-white font-bold btn-bounce"
            data-testid="post-task-btn"
          >
            <Plus size={18} className="mr-2" /> Post Task
          </Button>
        ) : (
          <Button
            onClick={() => navigate('/hours')}
            variant="outline"
            className="flex-1 rounded-2xl py-5 border-2 border-gray-200 font-bold btn-bounce"
            data-testid="hours-btn"
          >
            <FileText size={18} className="mr-2" /> My Hours
          </Button>
        )}
      </div>

      {/* Notifications */}
      {notifications.length > 0 && (
        <div className="mb-6 animate-fade-in-up" style={{ animationDelay: '300ms' }}>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-[#011627] flex items-center gap-2">
              <Bell size={18} className="text-[#FF9F1C]" /> Notifications
            </h2>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-sm font-bold text-[#2EC4B6]" data-testid="mark-all-read">
                Mark all read
              </button>
            )}
          </div>
          <div className="space-y-2">
            {notifications.slice(0, 3).map(n => (
              <div
                key={n.notif_id}
                className={`bg-white rounded-2xl p-3 border-2 flex items-center gap-3 ${
                  n.read ? 'border-gray-100' : 'border-[#2EC4B6]/30 bg-[#2EC4B6]/5'
                }`}
              >
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${n.read ? 'bg-gray-300' : 'bg-[#2EC4B6]'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-[#011627] truncate">{n.title}</p>
                  <p className="text-xs text-[#5C6B7F] truncate">{n.content}</p>
                </div>
                <span className="text-[10px] text-[#9AA5B1] flex-shrink-0">{timeAgo(n.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending Applications for Org */}
      {isOrg && pendingApps.length > 0 && (
        <div className="mb-6 animate-fade-in-up" style={{ animationDelay: '320ms' }}>
          <h2 className="text-lg font-bold text-[#011627] mb-3">Pending Applications</h2>
          <div className="space-y-2">
            {pendingApps.slice(0, 3).map(app => (
              <button
                key={app.app_id}
                onClick={() => navigate(`/opportunities/${app.opp_id}`)}
                className="w-full bg-white rounded-2xl p-3 border-2 border-[#FF9F1C]/20 flex items-center gap-3 text-left card-hover"
                data-testid={`pending-app-${app.app_id}`}
              >
                <div className="w-10 h-10 rounded-xl bg-[#FF9F1C]/10 flex items-center justify-center">
                  <Users size={18} className="text-[#FF9F1C]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-[#011627] truncate">{app.applicant_name}</p>
                  <p className="text-xs text-[#5C6B7F] truncate">applied for {app.opp_title}</p>
                </div>
                <ChevronRight size={16} className="text-[#9AA5B1]" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Recommended for You - Teen only */}
      {!isOrg && recommendations.length > 0 && (
        <div className="mb-6 animate-fade-in-up" style={{ animationDelay: '320ms' }}>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-[#011627]">
              <Sparkles size={18} className="inline mr-1 text-[#FF9F1C]" />
              Recommended for You
            </h2>
          </div>
          <div className="space-y-3 stagger-children">
            {recommendations.map(opp => (
              <div key={opp.opp_id} className="animate-fade-in-up">
                <GigCard opportunity={opp} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Featured Opportunities */}
      <div className="mb-6 animate-fade-in-up" style={{ animationDelay: '350ms' }}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-[#011627]">{isOrg ? 'Recent Opportunities' : 'Featured Gigs'}</h2>
          <button onClick={() => navigate('/explore')} className="text-sm font-bold text-[#2EC4B6] flex items-center" data-testid="see-all-opps">
            See All <ChevronRight size={16} />
          </button>
        </div>
        {opportunities.length === 0 ? (
          <div className="bg-white rounded-3xl p-8 border-2 border-gray-100 text-center">
            <Sparkles size={40} className="text-[#FF9F1C] mx-auto mb-3" />
            <p className="text-[#5C6B7F] font-semibold">No opportunities yet. Check back soon!</p>
          </div>
        ) : (
          <div className="space-y-3 stagger-children">
            {opportunities.slice(0, 4).map(opp => (
              <div key={opp.opp_id} className="animate-fade-in-up">
                <GigCard opportunity={opp} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Contact / Support Footer */}
      <div className="mb-8 text-center animate-fade-in-up" style={{ animationDelay: '400ms' }} data-testid="contact-footer">
        <div className="bg-white rounded-3xl p-5 border-2 border-gray-100">
          <Mail size={20} className="text-[#2EC4B6] mx-auto mb-2" />
          <p className="text-sm font-bold text-[#011627] mb-1">Questions or feedback?</p>
          <a
            href="mailto:teenhands.support@gmail.com"
            className="text-sm font-bold text-[#2EC4B6] hover:underline"
            data-testid="contact-email-link"
          >
            teenhands.support@gmail.com
          </a>
        </div>
      </div>

      {/* Setup Dialog for Google OAuth users */}
      <Dialog open={showSetup} onOpenChange={setShowSetup}>
        <DialogContent className="rounded-3xl border-2 border-gray-100 max-w-md" data-testid="setup-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Welcome! Let&apos;s set you up</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <Input
              placeholder="Your Name" value={setupForm.name}
              onChange={e => setSetupForm({ ...setupForm, name: e.target.value })}
              className="bg-gray-50 border-2 border-gray-100 focus:border-[#2EC4B6] rounded-2xl px-4 py-3 font-semibold"
              data-testid="setup-name"
            />
            <div className="flex gap-2">
              {['teen', 'organization'].map(r => (
                <button
                  key={r} type="button"
                  onClick={() => setSetupForm({ ...setupForm, role: r })}
                  className={`flex-1 py-3 rounded-2xl font-bold text-sm border-2 transition-all ${
                    setupForm.role === r ? 'border-[#2EC4B6] bg-[#2EC4B6]/10 text-[#2EC4B6]' : 'border-gray-100 text-[#5C6B7F]'
                  }`}
                  data-testid={`setup-role-${r}`}
                >
                  {r === 'teen' ? "I'm a Teen" : 'Organization'}
                </button>
              ))}
            </div>
            {setupForm.role === 'teen' && (
              <Input
                type="number" placeholder="Age (13-18)" min={13} max={18} value={setupForm.age}
                onChange={e => setSetupForm({ ...setupForm, age: e.target.value })}
                className="bg-gray-50 border-2 border-gray-100 focus:border-[#2EC4B6] rounded-2xl px-4 py-3 font-semibold"
                data-testid="setup-age"
              />
            )}
            <Button
              onClick={handleSetup}
              className="w-full rounded-full py-5 bg-[#2EC4B6] hover:bg-[#25A296] text-white font-bold text-lg btn-bounce"
              data-testid="setup-submit"
            >
              Let&apos;s Go!
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
